/**
 * City + service page content for the app, one chunk per city and language, so a
 * page downloads its own city's file instead of every city in both languages.
 * (./index.js keeps the eager registry the build scripts read.)
 *
 * index.js (the app entry) loads the current page's file before hydrating;
 * after an in-app navigation, CityServicePage shows a placeholder until its
 * file arrives. It does not suspend: switching language re-renders the same
 * page, and a suspended update hides it, which drops its react-helmet-async
 * tags for good (they register on first render only) — the FR page kept the
 * EN title and lost <html lang>.
 */
import { useEffect, useState } from "react";
import { CITIES } from "../cityServiceSlugMap";

/* Same files as ./index.js: when a new one is added there, add it here too. */
const FILES = {
  en: {
    montreal:        () => import("./montreal.en"),
    toronto:         () => import("./toronto.en"),
    ottawa:          () => import("./ottawa.en"),
    "quebec-city":   () => import("./quebec-city.en"),
    laval:           () => import("./laval.en"),
    "niagara-falls": () => import("./niagara-falls.en"),
  },
  fr: {
    montreal:      () => import("./montreal.fr"),
    "quebec-city": () => import("./quebec-city.fr"),
    laval:         () => import("./laval.fr"),
  },
};

const loaded = {};  // "en/montreal" -> that file's entries
const pending = {}; // "en/montreal" -> its load
const failed = {};  // "en/montreal" -> load error

/** Load one city's entries in one language (resolves at once if loaded already or there is no file). */
export function loadCityEntries(lang, cityKey) {
  const id = `${lang}/${cityKey}`;
  if (loaded[id] || !FILES[lang]?.[cityKey]) return Promise.resolve();
  pending[id] = pending[id] || FILES[lang][cityKey]().then(
    (m) => { loaded[id] = m.default; },
    (err) => { failed[id] = err; },
  );
  return pending[id];
}

/**
 * A city's entries in one language, keyed by service (as in ./index.js):
 * undefined while its file loads, null when the city has no file in that language.
 */
export function useCityEntries(lang, cityKey) {
  const id = `${lang}/${cityKey}`;
  const hasFile = Boolean(FILES[lang]?.[cityKey]);
  const [, setVersion] = useState(0);
  useEffect(() => {
    if (!hasFile || loaded[id] || failed[id]) return undefined;
    let live = true;
    loadCityEntries(lang, cityKey).then(() => { if (live) setVersion((v) => v + 1); });
    return () => { live = false; };
  }, [id, hasFile, lang, cityKey]);
  if (!hasFile) return null;
  if (failed[id]) throw failed[id];
  return loaded[id];
}

/** For index.js: load the entries of the city in a /:city/:service (or /fr/...) URL; null for other URLs. */
export function loadEntriesForPath(pathname) {
  const m = pathname.match(/^(\/fr)?\/([^/]+)\/[^/]+\/?$/);
  if (!m) return null;
  const lang = m[1] ? "fr" : "en";
  const cityKey = Object.keys(CITIES).find((k) => CITIES[k][lang] === m[2]);
  return cityKey ? loadCityEntries(lang, cityKey) : null;
}
